import { useState } from "react";
import { Header } from "@/components/layout/Header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ChevronDown, Search, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
const Referrals = () => {
  const [selectedVisits, setSelectedVisits] = useState<Record<string, Set<string>>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [openReportDialog, setOpenReportDialog] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();
  const {
    toast
  } = useToast();

  // Query to get referrals for the current school
  const {
    data: referralsData,
    refetch: refetchReferrals
  } = useQuery({
    queryKey: ['referrals', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return [];
      
      const {
        data: referrals,
        error
      } = await supabase.from('referrals').select(`
          *,
          student:students!inner (
            id,
            surname,
            other_names,
            class,
            stream,
            school_id,
            school:schools (
              name,
              prefix
            )
          ),
          nurse:profiles (
            name
          ),
          term:terms (
            name,
            academic_year:academic_years (
              name
            )
          )
        `)
        .eq('student.school_id', user.schoolId)
        .order('date', {
        ascending: false
      });
      if (error) throw error;
      return referrals;
    },
    enabled: !!user?.schoolId
  });

  // Query to get visits for each referral's student in the same term
  const {
    data: visitsData
  } = useQuery({
    queryKey: ['referralVisits', referralsData?.map(r => r.student_id)],
    queryFn: async () => {
      if (!referralsData?.length) return {};
      const visitsPromises = referralsData.map(async referral => {
        const {
          data: visits,
          error
        } = await supabase.from('visits').select(`
            *,
            nurse:profiles (
              name
            )
          `).eq('student_id', referral.student_id).eq('term_id', referral.term_id).order('date', {
          ascending: false
        });
        if (error) throw error;
        return {
          [referral.id]: visits
        };
      });
      const visitsResults = await Promise.all(visitsPromises);
      return visitsResults.reduce((acc, curr) => ({
        ...acc,
        ...curr
      }), {});
    },
    enabled: !!referralsData?.length
  });
  const toggleVisitSelection = (referralId: string, visitId: string) => {
    setSelectedVisits(prev => {
      const currentSet = prev[referralId] || new Set();
      const newSet = new Set(currentSet);
      if (newSet.has(visitId)) {
        newSet.delete(visitId);
      } else {
        newSet.add(visitId);
      }
      return {
        ...prev,
        [referralId]: newSet
      };
    });
  };
  const handleDeleteReferral = (referral: any) => {
    setReferralToDelete(referral);
    setDeleteDialogOpen(true);
  };
  const confirmDeleteReferral = async () => {
    if (!referralToDelete) return;
    setIsDeleting(true);
    try {
      const {
        error
      } = await supabase.from('referrals').delete().eq('id', referralToDelete.id);
      if (error) throw error;
      await refetchReferrals();
      toast({
        title: "Success",
        description: "Referral removed successfully"
      });
    } catch (error) {
      console.error("Error deleting referral:", error);
      toast({
        title: "Error",
        description: "Failed to remove referral",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setReferralToDelete(null);
    }
  };
  const generateReport = async (referral: any) => {
    try {
      const selectedVisitIds = selectedVisits[referral.id] || new Set();
      const visits = (visitsData?.[referral.id] || []).filter(visit => selectedVisitIds.has(visit.id));
      if (visits.length === 0) {
        toast({
          title: "Error",
          description: "Please select at least one visit to include in the report",
          variant: "destructive"
        });
        return;
      }

      // Create the Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [new Paragraph({
            children: [new TextRun({
              text: `${referral.student.school.name} - ${referral.student.school.prefix}`,
              bold: true,
              size: 32
            })],
            spacing: {
              after: 400
            }
          }), new Paragraph({
            children: [new TextRun({
              text: "MEDICAL REFERRAL REPORT",
              bold: true,
              size: 28
            })],
            spacing: {
              after: 400
            }
          }), new Paragraph({
            children: [new TextRun({
              text: "Student Information",
              bold: true,
              size: 24
            })],
            spacing: {
              after: 200
            }
          }), new Paragraph({
            children: [new TextRun({
              text: `Name: ${referral.student.surname}${referral.student.other_names ? `, ${referral.student.other_names}` : ""}`
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Class: ${referral.student.class}${referral.student.stream ? ` Stream: ${referral.student.stream}` : ""}`
            })],
            spacing: {
              after: 400
            }
          }), new Paragraph({
            children: [new TextRun({
              text: "Referral Details",
              bold: true,
              size: 24
            })],
            spacing: {
              after: 200
            }
          }), new Paragraph({
            children: [new TextRun({
              text: `Hospital: ${referral.hospital}`
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Reason: ${referral.reason}`
            })],
            spacing: {
              after: 400
            }
          }), new Paragraph({
            children: [new TextRun({
              text: "Visit History",
              bold: true,
              size: 24
            })],
            spacing: {
              after: 200
            }
          }), ...visits.flatMap(visit => [new Paragraph({
            children: [new TextRun({
              text: `Date: ${format(new Date(visit.date), 'PPP')}`,
              bold: true
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Temperature: ${visit.temperature}°C`
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Complaints: ${visit.complaint}`
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Diagnosis: ${visit.diagnosis}`
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Treatment: ${visit.treatment}`
            })]
          }), new Paragraph({
            children: [new TextRun({
              text: `Nurse: ${visit.nurse.name}`
            })],
            spacing: {
              after: 400
            }
          })])]
        }]
      });

      // Generate and save the document
      const blob = await Packer.toBlob(doc);
      const fileName = `${referral.student.surname}_referral_${format(new Date(), 'yyyy-MM-dd')}.docx`;
      saveAs(blob, fileName);
      setOpenReportDialog(null);
      toast({
        title: "Success",
        description: "Referral report generated successfully"
      });
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: "Failed to generate referral report",
        variant: "destructive"
      });
    }
  };

  // Filter referrals based on search query
  const filteredReferrals = referralsData?.filter(referral => {
    const studentName = `${referral.student.surname} ${referral.student.other_names || ''}`.toLowerCase();
    const searchTerm = searchQuery.toLowerCase();
    return studentName.includes(searchTerm);
  });

  // Calculate summary statistics from filtered referrals
  const summaryStats = {
    total: filteredReferrals?.length || 0,
    pending: filteredReferrals?.filter(r => r.status === 'PENDING').length || 0,
    completed: filteredReferrals?.filter(r => r.status === 'COMPLETED').length || 0
  };
  return <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto p-6 pt-[160px] space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold my-[19px]">Referrals</h1>
          
          <div className="flex items-center gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input placeholder="Search by student name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            
            <Card className="p-4">
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="font-semibold">Total:</span> {summaryStats.total}
                </div>
                <div>
                  <span className="font-semibold">Pending:</span> {summaryStats.pending}
                </div>
                <div>
                  <span className="font-semibold">Completed:</span> {summaryStats.completed}
                </div>
              </div>
            </Card>
          </div>
        </div>
        
        <div className="space-y-6">
          {filteredReferrals?.map(referral => <Collapsible key={referral.id}>
              <Card className="p-6">
                <CollapsibleTrigger className="flex justify-between items-center w-full">
                  <div>
                    <h2 className="text-2xl font-bold">
                      {referral.student.surname}
                      {referral.student.other_names ? `, ${referral.student.other_names}` : ""}
                    </h2>
                    <p className="text-muted-foreground text-left mx-[2px] text-lg font-bold">
                      Class: {referral.student.class}
                      {referral.student.stream ? ` Stream: ${referral.student.stream}` : ""}
                    </p>
                  </div>
                  <ChevronDown className="h-6 w-6 text-muted-foreground transition-transform ui-expanded:rotate-180" />
                </CollapsibleTrigger>

                <CollapsibleContent className="pt-4">
                  <div className="space-y-4">
                    {/* Referral Information */}
                    <div className="grid gap-2">
                      <p><strong>Hospital:</strong> {referral.hospital}</p>
                      <p><strong>Reason:</strong> {referral.reason}</p>
                      <p><strong>Date:</strong> {format(new Date(referral.date), 'PPP')}</p>
                      <p><strong>Referred by:</strong> {referral.nurse.name}</p>
                    </div>

                    {/* Visits Table */}
                    <Dialog open={openReportDialog === referral.id} onOpenChange={open => setOpenReportDialog(open ? referral.id : null)}>
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Visit History</h3>
                        <div className="flex gap-2">
                          <Button variant="default" onClick={() => setOpenReportDialog(referral.id)}>
                            Generate Report
                          </Button>
                          <Button variant="destructive" size="icon" onClick={() => handleDeleteReferral(referral)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <DialogContent className="max-w-4xl">
                        <DialogHeader>
                          <DialogTitle>Select Visits for Report</DialogTitle>
                        </DialogHeader>
                        <div className="max-h-[60vh] overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[50px]">Select</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Temperature</TableHead>
                                <TableHead>Complaints</TableHead>
                                <TableHead>Diagnosis</TableHead>
                                <TableHead>Treatment</TableHead>
                                <TableHead>Nurse</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {visitsData?.[referral.id]?.map(visit => <TableRow key={visit.id}>
                                  <TableCell>
                                    <Checkbox checked={selectedVisits[referral.id]?.has(visit.id)} onCheckedChange={() => toggleVisitSelection(referral.id, visit.id)} />
                                  </TableCell>
                                  <TableCell>{format(new Date(visit.date), 'PPP')}</TableCell>
                                  <TableCell>{visit.temperature}°C</TableCell>
                                  <TableCell>{visit.complaint}</TableCell>
                                  <TableCell>{visit.diagnosis}</TableCell>
                                  <TableCell>{visit.treatment}</TableCell>
                                  <TableCell>{visit.nurse.name}</TableCell>
                                </TableRow>)}
                            </TableBody>
                          </Table>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setOpenReportDialog(null)}>
                            Cancel
                          </Button>
                          <Button variant="default" onClick={() => generateReport(referral)}>
                            Download Report
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Temperature</TableHead>
                          <TableHead>Complaints</TableHead>
                          <TableHead>Diagnosis</TableHead>
                          <TableHead>Treatment</TableHead>
                          <TableHead>Nurse</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visitsData?.[referral.id]?.map(visit => <TableRow key={visit.id}>
                            <TableCell>{format(new Date(visit.date), 'PPP')}</TableCell>
                            <TableCell>{visit.temperature}°C</TableCell>
                            <TableCell>{visit.complaint}</TableCell>
                            <TableCell>{visit.diagnosis}</TableCell>
                            <TableCell>{visit.treatment}</TableCell>
                            <TableCell>{visit.nurse.name}</TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>)}

          {!filteredReferrals?.length && <Card className="p-6">
              <p className="text-center text-muted-foreground">
                {searchQuery ? "No referrals found matching your search" : "No referrals found"}
              </p>
            </Card>}
        </div>
      </main>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Referral</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this referral for {referralToDelete?.student?.surname}
              {referralToDelete?.student?.other_names ? `, ${referralToDelete.student.other_names}` : ""}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReferralToDelete(null)} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteReferral} disabled={isDeleting}>
              {isDeleting ? <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </> : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default Referrals;